import { parse, isValid, addDays, format } from 'date-fns';
import { UNIVERSAL_DATE_FORMAT } from './dates';
/**
 * Utility functions for money calculations with proper decimal handling
 */
export const moneyUtils = {
    // Convert to a unified string format with 2 decimal places
    toUniversal: (value) => {
        const num = typeof value === 'string' ? parseFloat(value) : value;
        return isNaN(num) ? '0.00' : num.toFixed(2);
    },
    // Convert money value to cents (integer) to avoid floating point issues
    toCents: (value) => {
        return Math.round(parseFloat(value) * 100);
    },
    // Convert cents back to money format
    fromCents: (cents) => {
        return (cents / 100).toFixed(2);
    },
    // Add two money values safely
    add: (a, b) => {
        const aCents = moneyUtils.toCents(a);
        const bCents = moneyUtils.toCents(b);
        return moneyUtils.fromCents(aCents + bCents);
    },
    // Subtract two money values safely
    subtract: (a, b) => {
        const aCents = moneyUtils.toCents(a);
        const bCents = moneyUtils.toCents(b);
        return moneyUtils.fromCents(aCents - bCents);
    },
    // Multiply money value by a factor
    multiply: (value, factor) => {
        const cents = moneyUtils.toCents(value);
        return moneyUtils.fromCents(Math.round(cents * factor));
    },
    // Format money for display
    format: (amount) => {
        const value = typeof amount === 'string' ? parseFloat(amount) : amount;
        return value.toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });
    }
};
/**
 * Calculate total for a single line item
 */
export const calculateLineItemTotal = (item) => {
    const quantity = parseFloat(item.quantity) || 0;
    const price = moneyUtils.toUniversal(item.price);
    return moneyUtils.multiply(price, quantity);
};
/**
 * Calculate invoice totals including subtotal, discount, VAT and final total
 */
export const calculateInvoiceTotals = (items, discount) => {
    // Calculate subtotal
    const subtotal = items.reduce((sum, item) => {
        const itemTotal = calculateLineItemTotal(item);
        return moneyUtils.add(sum, itemTotal);
    }, '0.00');
    // Calculate discount
    const discountAmount = discount.type === 'none' ? '0.00' :
        discount.type === 'percentage' ?
            moneyUtils.multiply(subtotal, parseFloat(discount.value) / 100 || 0) :
            moneyUtils.toUniversal(discount.value);
    // Apply discount to subtotal
    const discountedSubtotal = moneyUtils.subtract(subtotal, discountAmount);
    // Calculate VAT on discounted amount
    const vat = items.reduce((sum, item) => {
        const itemTotal = calculateLineItemTotal(item);
        const itemShare = moneyUtils.toCents(subtotal) > 0 ?
            moneyUtils.toCents(itemTotal) / moneyUtils.toCents(subtotal) : 0;
        const itemDiscount = moneyUtils.multiply(discountAmount, itemShare);
        const discountedItemTotal = moneyUtils.subtract(itemTotal, itemDiscount);
        const vatRate = parseFloat(item.vatRate) / 100 || 0;
        return moneyUtils.add(sum, moneyUtils.multiply(discountedItemTotal, vatRate));
    }, '0.00');
    const total = moneyUtils.add(discountedSubtotal, vat);
    return { subtotal, discountAmount, vat, total };
};
/**
 * Calculate due date based on payment terms
 */
export const calculateDueDate = (invoiceDate, paymentTerms) => {
    try {
        // Parse the date (expecting YYYY-MM-DD format)
        const date = parse(invoiceDate, UNIVERSAL_DATE_FORMAT, new Date());
        if (!isValid(date))
            return invoiceDate;
        const daysMatch = paymentTerms.match(/(\d+)\s*days?/i);
        let resultDate = date;
        if (daysMatch) {
            resultDate = addDays(date, parseInt(daysMatch[1], 10));
        }
        else if (paymentTerms.toLowerCase() === 'due on receipt') {
            resultDate = date; // Same date
        }
        else {
            resultDate = addDays(date, 14); // Default to 14 days
        }
        // Return in YYYY-MM-DD format
        return format(resultDate, UNIVERSAL_DATE_FORMAT);
    }
    catch (e) {
        console.error('Error calculating due date:', e);
        return invoiceDate;
    }
};
/**
 * Generate next invoice number based on settings
 */
export const generateNextInvoiceNumber = (settings) => {
    const year = new Date().getFullYear();
    const yearStr = settings.invoiceNumberFormat.yearFormat === 'full' ? year.toString() : year.toString().slice(-2);
    // Get the next number based on latestInvoiceNumber
    const nextNum = (settings.latestInvoiceNumber || 0) + 1;
    const parts = [
        settings.invoiceNumberFormat.prefix,
        settings.invoiceNumberFormat.includeYear ? yearStr : null,
        nextNum.toString().padStart(settings.invoiceNumberFormat.numberPadding, '0')
    ].filter(Boolean);
    return parts.join(settings.invoiceNumberFormat.separator);
};
/**
 * Compare if two company objects have the same field values
 */
export const isSameCompany = (a, b) => {
    // Get all keys except isPlaceholder
    const keys = Object.keys(a).filter(key => key !== 'isPlaceholder');
    // Compare all fields
    return keys.every(key => a[key] === b[key]);
};
